import { db } from '../database/schema.js'
import type { Feature } from '../schemas/feature.js'
import type { NodeRef, Predicate } from '../schemas/edge.js'
import { edgeService } from './edge-service.js'

export interface FeatureSnapshot {
  id: string
  before: Feature | null
  after: Feature | null
}

export interface EdgeSnapshot {
  subject: NodeRef
  predicate: Predicate
  object: NodeRef
  action: 'created' | 'deleted'
}

export interface UndoOperation {
  type: 'create' | 'delete' | 'edit' | 'split' | 'join' | 'delete-multiple'
  label: string
  features: FeatureSnapshot[]
  edges: EdgeSnapshot[]
}

export interface UndoDeps {
  renderFeatureOnMap: (feature: Feature) => void
  removeShapeFromMap: (featureId: string) => void
  updateFeatureOnMap: (featureId: string, feature: Feature) => void
}

const MAX_SIZE = 50

class UndoStack {
  private stack: UndoOperation[] = []
  private pointer = -1
  private pendingEdit: { featureId: string; snapshot: Feature } | null = null

  push(op: UndoOperation): void {
    this.stack = this.stack.slice(0, this.pointer + 1)
    this.stack.push(op)
    if (this.stack.length > MAX_SIZE) {
      this.stack.shift()
    } else {
      this.pointer++
    }
  }

  undo(): UndoOperation | null {
    if (this.pointer < 0) return null
    return this.stack[this.pointer--]
  }

  redo(): UndoOperation | null {
    if (this.pointer >= this.stack.length - 1) return null
    return this.stack[++this.pointer]
  }

  canUndo(): boolean {
    return this.pointer >= 0
  }

  canRedo(): boolean {
    return this.pointer < this.stack.length - 1
  }

  clear(): void {
    this.stack = []
    this.pointer = -1
    this.pendingEdit = null
  }

  setPendingEdit(featureId: string, snapshot: Feature): void {
    this.pendingEdit = { featureId, snapshot }
  }

  getPendingEdit(): { featureId: string; snapshot: Feature } | null {
    return this.pendingEdit
  }

  clearPendingEdit(): void {
    this.pendingEdit = null
  }
}

export const undoStack = new UndoStack()

export async function executeUndo(op: UndoOperation, deps: UndoDeps): Promise<void> {
  for (const feat of op.features) {
    if (feat.before === null) {
      await db.features.delete(feat.id)
      deps.removeShapeFromMap(feat.id)
    } else if (feat.after === null) {
      await db.features.add(feat.before)
      deps.renderFeatureOnMap(feat.before)
    } else {
      await db.features.update(feat.id, feat.before)
      deps.updateFeatureOnMap(feat.id, feat.before)
    }
  }

  for (const edge of op.edges) {
    if (edge.action === 'created') {
      await edgeService.deleteExact(edge.subject, edge.predicate, edge.object)
    } else {
      await edgeService.create({
        subject: edge.subject,
        predicate: edge.predicate,
        object: edge.object,
      })
    }
  }
}

export async function executeRedo(op: UndoOperation, deps: UndoDeps): Promise<void> {
  for (const feat of op.features) {
    if (feat.before === null) {
      await db.features.add(feat.after!)
      deps.renderFeatureOnMap(feat.after!)
    } else if (feat.after === null) {
      await db.features.delete(feat.id)
      deps.removeShapeFromMap(feat.id)
    } else {
      await db.features.update(feat.id, feat.after)
      deps.updateFeatureOnMap(feat.id, feat.after)
    }
  }

  for (const edge of op.edges) {
    if (edge.action === 'created') {
      await edgeService.create({
        subject: edge.subject,
        predicate: edge.predicate,
        object: edge.object,
      })
    } else {
      await edgeService.deleteExact(edge.subject, edge.predicate, edge.object)
    }
  }
}
